import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'react-feather';
import {
  AthleteSnapshot,
  auditProfileCompleteness,
} from '../hooks/useProfileCompleteness';
import analytics from '../services/analytics';

/**
 * ProfileCompletenessPill
 *
 * Persistent "X items left on your profile" pill for the dashboard. Unlike
 * ProfileGapBanner, this is NOT dismissable — once dismissed the banner
 * stops nagging, but the user lost the at-a-glance reminder of how much
 * is left to fill in. The pill survives dismissal and stays until the
 * profile has no recommended-tier gaps.
 *
 * Total = sum of missingRecommended across every section the audit
 * reports. Tap navigates to /profile; the JIT router on that page
 * lists every gap (subject to its own per-session dedupe).
 */

interface Props {
  athlete: AthleteSnapshot;
}

const ProfileCompletenessPill: React.FC<Props> = ({ athlete }) => {
  const navigate = useNavigate();
  const audit = auditProfileCompleteness(athlete);
  const total = audit.sectionsWithRecommendedGaps.reduce(
    (sum, s) => sum + s.missingRecommended.length,
    0,
  );

  if (total === 0) return null;

  const handleClick = () => {
    try {
      analytics.track('profile_completeness_pill_clicked', {
        total_missing: total,
        sections: audit.sectionsWithRecommendedGaps.map((s) => s.key),
      });
    } catch {}
    navigate('/profile');
  };

  const label = total === 1 ? '1 item left on your profile' : `${total} items left on your profile`;

  return (
    <button
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px 6px 14px',
        marginBottom: 12,
        background: 'white',
        border: '1px solid rgba(249, 85, 13, 0.4)',
        borderRadius: 999,
        fontFamily: 'Manrope, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        color: '#151133',
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(21, 17, 51, 0.04)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 22,
          height: 22,
          padding: '0 6px',
          background: '#F9550D',
          color: 'white',
          borderRadius: 11,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {total}
      </span>
      <span>{label}</span>
      <ChevronRight size={14} color="rgba(21, 17, 51, 0.6)" />
    </button>
  );
};

export default ProfileCompletenessPill;
